import type { TargetingRule } from "./feature-flags-types";

/**
 * The rule id this console gives the one rule it manages. Kept stable so a
 * flag edited here twice updates its rule rather than growing a second one.
 *
 * It is NOT how a rule is recognised: {@link classifyServeRule} matches on
 * shape, so a rule created before this UI existed (or by hand against the API)
 * is still editable here.
 */
export const SIMPLE_SERVE_RULE_ID = "console-serve-when-enabled";

export type ServeRuleState =
  /**
   * No rules at all. The flag returns its default variation in every
   * environment, whatever the per-environment toggles say, because the
   * evaluator has nothing to match. An operator turning it "on" sees nothing
   * happen and no explanation.
   */
  | { readonly kind: "inert" }
  /** Exactly one unconditional, full-rollout rule: safe to edit from a form. */
  | { readonly kind: "simple"; readonly rule: TargetingRule }
  /**
   * Conditions, segments, a partial rollout, or more than one rule. Someone
   * expressed targeting the two-field form cannot represent, so the form must
   * refuse rather than flatten it.
   */
  | { readonly kind: "complex"; readonly rules: readonly TargetingRule[] };

function isSimpleShape(rule: TargetingRule): boolean {
  return rule.conditions.length === 0 && rule.segmentKeys.length === 0 && rule.rolloutPct === 100;
}

/**
 * Decide whether a flag's rules can be expressed as "when enabled, serve X".
 *
 * The console offers one control over what a flag serves. That is enough for
 * the flags in use, and it is the thing that was missing: without a rule a flag
 * cannot serve anything but its default. What it must never do is present
 * real targeting as if it were that one control, because saving would then
 * overwrite it.
 */
export function classifyServeRule(rules: readonly TargetingRule[]): ServeRuleState {
  if (rules.length === 0) return { kind: "inert" };
  const [only] = rules;
  if (rules.length === 1 && only && isSimpleShape(only)) {
    return { kind: "simple", rule: only };
  }
  return { kind: "complex", rules };
}
